import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../helpers/notification'
import {
  makeTextMaterialFormState,
  mapApiDataToState,
  mapStateToApiVariable,
  TextMaterialFormState
} from './TextMaterialFormState'

import { Checkbox, Col, DatePicker, Form, Grid, InputGroup, Panel, Row, SelectPicker, Tag, TagPicker, Toggle, Button, Message } from 'rsuite'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import { useFetchSingleTextMaterial } from '@@partner:graphql/query/textMaterial/textMaterial/singleTextMaterial.gql'
import { clearDraft, getSearchableTextMaterialStatus, loadDraft, saveDraft } from '@@partner:helpers/textMaterial'
import { useMutateUpdateTextMaterial } from '@@partner:graphql/mutation/textMaterial/updateTextMaterial.gql'

import EditorJS from '@editorjs/editorjs'
import Header from '@editorjs/header'
import List from '@editorjs/list'
import Paragraph from '@editorjs/paragraph'
import ImageTool from '@editorjs/image'
import RawTool from '@editorjs/raw'
import YoutubeEmbed from 'editorjs-youtube-embed'
import Quote from '@editorjs/quote'
import ImageGallery from '@kiberpro/editorjs-gallery'
import Alert from 'editorjs-alert'

import './TextMaterial.scss'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import { useFetchAllTextMaterialTypes } from '@@partner:graphql/query/textMaterial/types/allTextMaterialTypes.gql'
import { API_URL_UPLOAD_MATERIAL_PREVIEW_IMAGE } from '@@partner:config/api'
import { CUSTOMER_FRONTEND_URL } from '@@partner:config'
import { useFetchAllTextMaterialCategories } from '@@partner:graphql/query/textMaterial/categories/allTextMaterialCategories.gql'
import DropzonePreview from '@components/common/form/DropzonePreview'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import { UPLOAD_NAME_TEXT_MATERIAL_IMAGE } from '@@partner:config/textMaterial'
import ProductCarousel from './editorjs/productCarousel/ProductCarousel'
import { useFetchAllProductGroups } from '@@partner:graphql/query/productGroup/allProductGroups.gql'
import { ROUTE_PRODUCT_GROUPS_EDIT } from '@@partner:config/routes'
import { useDebounce, useDebouncedCallback } from 'use-debounce'
import ProductCard from './editorjs/productCard/ProductCard'
import Sortable from 'sortablejs'
import TextMaterialTagPicker from './tag/TagPicker'
import ThemeCheckPicker from './theme/ThemeCheckPicker'

export interface CategoryFormProps extends CrudFormProps {
  brandSlug: string
  textId: GQL_ID
}

const AVAILABLE_TEXT_CATEGORY_STATUSES = getSearchableTextMaterialStatus()


const uploadImage = async (file: File, imageStyle?: string) => {
  const formData = new FormData()
  formData.append('text-material', file, file.name)
  
  // Добавляем параметр размера, если передан
  if (imageStyle) {
    formData.append('style', imageStyle)
  }
  
  const response = await fetch(API_URL_UPLOAD_MATERIAL_PREVIEW_IMAGE, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  })

  if (!response.ok) {
    return {
      success: 0
    }
  }

  const data = await response.json()

  return {
    success: 1,
    file: {
      url: data.url
    }
  }
}

// Функция загрузки для галереи с оригинальным размером
const uploadImageForGallery = async (file: File) => {
  return uploadImage(file, 'orig_')
}

// Функция загрузки для обычных изображений (по умолчанию)
const uploadImageDefault = async (file: File) => {
  return uploadImage(file, 'orig_')
}

export default function TextMaterialForm ({
  brandSlug,
  textId,
  onSaved
}: CategoryFormProps) {
  const editorRef = useRef<EditorJS | null>(null)

  const { formState, setFormState, setFormRef } =
    useValidatableForm<TextMaterialFormState>(makeTextMaterialFormState)
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>('ru')
  const [ isEditorReady, setIsEditorReady ] = useState(false)
  const [ showDraftBanner, setShowDraftBanner ] = useState(false)
  const [ draftData, setDraftData ] = useState<any>(null)
  const [ hasInitialized, setHasInitialized ] = useState(false)

  const debounced = useDebouncedCallback(
    (value) => {
      if (hasInitialized) {
        saveDraft(textId, value)
      }
    },
    1000,
    { maxWait: 5000 }
  )
  
  useEffect(() => {
    debounced(formState)
  }, [ formState, debounced, hasInitialized ])

  const {
    data,
    loading: loadingTextMaterial,
    error: errorLoadingTextMaterial
  } = useFetchSingleTextMaterial({
    id: textId
  })

  const { 
    data: typesData,
    loading: loadingTypes,
    error: errorLoadingTextMaterialTypes
  } = useFetchAllTextMaterialTypes()

  const {
    data: categoriesData,
    loading: loadingCategories,
    error: errorLoadingCategories
  } = useFetchAllTextMaterialCategories()

  const { 
    data: productGroupsData,
    loading: loadingProductGroups,
    error: errorLoadingProductGroups
  } = useFetchAllProductGroups({
    filter: {
      and: [
        {
          fields: [ 'status' ],
          op: 'eq',
          value: [ 'active' ]
        }
      ]
    },
    pager: {
      page: 1,
      limit: 24
    }
  })

  const types = typesData?.allTextMaterialTypes
  const categories = categoriesData?.allTextMaterialCategories

  const [ saveTextMaterial, { loading: loadingSaveTextCategory, error: errorSavingTextCategory } ] =
    useMutateUpdateTextMaterial()

  // Загружаем данные с сервера
  useEffect(() => {
    if (!loadingTextMaterial && !hasInitialized) {
      // Если есть данные с сервера - загружаем их
      if (data?.singleTextMaterial) {
        // Сначала загружаем данные с сервера
        const serverData = mapApiDataToState(data.singleTextMaterial)
        setFormState((state) => mergeLeft(state, serverData))
        
        // Затем проверяем, есть ли черновик
        const draft = loadDraft(textId)
        
        if (draft) {
          // Показываем баннер вместо confirm
          setDraftData(draft)
          setShowDraftBanner(true)
        }
      }
      // Для нового материала или когда данных нет - тоже инициализируем
      
      setHasInitialized(true)
    }
  }, [ data?.singleTextMaterial, loadingTextMaterial, setFormState, textId, hasInitialized ])

  // Устанавливаем готовность редактора после инициализации данных
  useEffect(() => {
    if (hasInitialized && !loadingProductGroups) {
      setIsEditorReady(true)
    }
  }, [ hasInitialized, loadingProductGroups ])

  const handleGoToProductGroup = useCallback(
    (groupId: GQL_ID) => {
      window.open(`${ROUTE_PRODUCT_GROUPS_EDIT.replace(':groupId', `${groupId}`).replace(':brandSlug', brandSlug)}`, '_blank')
    },
    [ brandSlug ]
  )

  const handleRestoreDraft = useCallback(() => {
    if (draftData) {
      setFormState((state) => mergeLeft(state, draftData))
      setShowDraftBanner(false)
      setDraftData(null)
    }
  }, [ draftData, setFormState ])

  const handleIgnoreDraft = useCallback(() => {
    clearDraft(textId)
    setShowDraftBanner(false)
    setDraftData(null)
  }, [ textId ])
  
  
  useEffect(() => {
    if (!isEditorReady) {
      return
    }

    const currentTranslation = formState.i18n.find((translation) => translation.languageCode === selectedLanguage)
    const editorStringifiedData = currentTranslation?.content

    editorRef.current = new EditorJS({
      holder: 'editor',
      tools: {
        header: {
          class: Header,
          inlineToolbar: true,
          config: {
            levels: [ 1, 2, 3 ],
            defaultLevel: 2,
          },
        },
        list: {
          class: List,
          inlineToolbar: true
        },
        paragraph: {
          class: Paragraph,
          inlineToolbar: true,
          placeholder: 'Обычный текст',
          preserveBlank: false
        },
        quote: {
          class: Quote,
          inlineToolbar: true,
          config: {
            quotePlaceholder: 'Введите текст цитаты',
            captionPlaceholder: 'Quote\'s author',
          }
        },
        alert: {
          class: Alert,
          inlineToolbar: true,
          config: {
            alertTypes: [ 'primary', 'secondary', 'info', 'success', 'warning', 'danger', 'light', 'dark' ], 
            defaultType: 'primary',
            messagePlaceholder: 'Введите сообщение',
          }
        },
        image: {
          class: ImageTool,
          config: {
            uploader: {

              /**
               * Upload file to the server and return an uploaded image data
               * @param {File} file - file selected from the device or pasted by drag-n-drop
               * @return {Promise.<{success, file: {url}}>}
               */

              uploadByFile: uploadImageDefault
            }
          }
        },
        gallery: {
          class: ImageGallery,
          config: {
            sortableJs: Sortable,
            uploader: {

              /**
               * Upload file to the server and return an uploaded image data
               * @param {File} file - file selected from the device or pasted by drag-n-drop
               * @return {Promise.<{success, file: {url}}>}
               */

              uploadByFile: uploadImageForGallery
            }
          }
        },
        raw: RawTool,
        youtube: YoutubeEmbed,
        productList: {
          class: ProductCarousel,
          config: {
            groupList: productGroupsData?.allProductGroups.edges || [],
            onViewGroup: handleGoToProductGroup
          }
        },
        productCard: ProductCard,
      },
      data: editorStringifiedData ? JSON.parse(editorStringifiedData) : undefined,
      onChange: async () => {
        if (editorRef.current) {
          const content = await editorRef.current.save()
          setFormState((state) => {
            const newI18n = [ ...state.i18n ]
            const translationIndex = newI18n.findIndex((translation) => translation.languageCode === selectedLanguage)
            if (translationIndex > -1) {
              newI18n[translationIndex].content = JSON.stringify(content) // Сохраняем в state
            }
            else {
              newI18n.push({ languageCode: selectedLanguage, title: '', lead: '', content: JSON.stringify(content) }) // Сохраняем в state
            }

            return mergeLeft(state, { i18n: newI18n })
          })
        }
      },
      i18n: {
        messages: {
          toolNames: {
            'Text': 'Текст',
            'Heading': 'Заголовок',
            'Unordered List': 'Маркированный список',
            'Ordered List': 'Нумерованный список',
            'Checklist': 'Чеклист',
            'Quote': 'Цитата',
            'Alert': 'Алерт',
            'Image': 'Изображение',
            'Gallery': 'Галерея',
            'Raw HTML': 'HTML код',
            'Youtube': 'Youtube',
            'Список товаров': 'Список товаров'
          }
        }
      }
    })

    return () => {
      editorRef.current?.destroy()
    }
  }, [ isEditorReady, selectedLanguage ])

  const handleUnlinkPreviewClick = useCallback(() => {
    setFormState((state) =>
      mergeLeftOnly(
        [ 'preview_id', 'preview', 'url' ],
        state,
        makeTextMaterialFormState()
      )
    )
  }, [ setFormState ])

  const handlePreviewUploaded = useCallback(
    (preview_id, url) => {
      setFormState((state) =>
        mergeLeftOnly([ 'preview_id', 'preview', 'url' ], state, {
          preview_id,
          preview: { url }
        })
      )
    },
    [ setFormState ]
  )

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )
  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveTextMaterial({
          variables: {
            textMaterialInfo: mapStateToApiVariable(formState),
            upsert: textId ? false : true
          }
        })
          .then(({ data }) => {
            if (data?.updateTextMaterial) {
              notifyOk('Статья сохранена')
              setFormState((state) =>
                mergeLeftOnly([ 'id' ], state, mapApiDataToState(data.updateTextMaterial))
              )
              clearDraft(textId)
              onSaved?.(data?.updateTextMaterial.id)
            }
            
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveTextMaterial, formState, setFormState, onSaved, textId ]
  )

  // Функция для генерации ссылки на материал
  const generateMaterialUrl = useCallback(() => {
    if (!formState.slug || !categoriesData?.allTextMaterialCategories) {
      return ''
    }
    
    const category = categoriesData.allTextMaterialCategories.find(
      cat => cat.id === formState.category_id
    )
    
    if (!category?.slug) {
      return ''
    }
    
    return `${CUSTOMER_FRONTEND_URL}/media/category/${category.slug}/${formState.slug}`
  }, [ formState.slug, formState.category_id, categoriesData?.allTextMaterialCategories ])

  if (errorLoadingTextMaterial) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить материал #${textId} для редактирования`}
        error={errorLoadingTextMaterial}
      />
    )
  }

  if (loadingTextMaterial) {
    return <AppLoader content={'Загрузка материала' } />
  }

  if (errorLoadingTextMaterial) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить данные материала #${textId} для редактирования`}
        error={errorLoadingTextMaterial}
      />
    )
  }

  const previewUrl = formState.preview?.url ? formState.preview?.url : null

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      layout='vertical'
      fluid
    >
      
      {showDraftBanner && (
        <Message 
          type="info" 
          className="mb-4"
          closable 
          onClose={() => setShowDraftBanner(false)}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <strong>Найден черновик</strong> - обнаружены несохраненные изменения. Восстановить их?
            </div>
            <div style={{ marginLeft: '16px' }}>
              <Button 
                size="xs" 
                appearance="primary" 
                onClick={handleRestoreDraft}
                style={{ marginRight: '8px' }}
              >
                Восстановить
              </Button>
              <Button 
                size="xs" 
                appearance="subtle" 
                onClick={handleIgnoreDraft}
              >
                Игнорировать
              </Button>
            </div>
          </div>
        </Message>
      )}

      <Grid fluid>
        <Col xs={24} md={16}>
          <Col xs={24}>
            <Panel 
              shaded
              className='my-5'
            >
              <Row className='mb-3'>
                <Form.Group>
                  <Form.ControlLabel>Название (техническое)</Form.ControlLabel>
                  <Form.Control 
                    name="name" 
                    placeholder="Name" 
                  />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Имя в URL</Form.ControlLabel>
                  <Form.Control 
                    name="slug" 
                    placeholder="Имя в URL" 
                  />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Ссылка на материал</Form.ControlLabel>
                  {generateMaterialUrl() ? (
                    <div style={{ padding: '8px 12px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                      <a href={generateMaterialUrl()} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'none' }}>
                        {generateMaterialUrl()}
                      </a>
                    </div>
                  ) : (
                    <div style={{ padding: '8px 12px', color: '#6c757d', fontStyle: 'italic' }}>
                      Заполните слаг и выберите категорию для генерации ссылки
                    </div>
                  )}
                </Form.Group>
                
              </Row>
              <Row className='mb-3'>
                <Col>
                  <Form.Group>
                    <Form.ControlLabel>Статус</Form.ControlLabel>
                    <Form.Control 
                      name='status' 
                      value={formState.status}
                      accepter={SelectPicker} 
                      data={AVAILABLE_TEXT_CATEGORY_STATUSES} 
                      style={{ width: 'auto' }}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.ControlLabel>Тип</Form.ControlLabel>
                    { types && (
                      <SelectPicker
                        data={types}
                        value={formState.type_id}
                        cleanable={false}
                        onSelect={(value) => {
                          setFormState((state) => mergeLeft(state, { type_id: value }))
                        }}
                        labelKey='name'
                        valueKey='id'
                        style={{ width: 'auto' }}
                      />
                    )}
                  </Form.Group>
                </Col>
                { categories && (
                  <Col>
                    <Form.Group>
                      <Form.ControlLabel>Категория</Form.ControlLabel>
                      <SelectPicker
                        data={categories}
                        value={formState.category_id}
                        cleanable={false}
                        onSelect={(value) => {
                          setFormState((state) => mergeLeft(state, { category_id: value }))
                        }}
                        labelKey='name'
                        valueKey='id'
                        style={{ width: 'auto' }}
                      />
                    </Form.Group>
                  </Col>
                )}
                <Col>
                  <Form.ControlLabel>Период действия</Form.ControlLabel>
                  <InputGroup>
                    <Form.Control
                      accepter={DatePicker}
                      block
                      oneTap
                      isoWeek
                      appearance="subtle"
                      name="activeFrom"
                      value={formState.activeFrom}
                      format="yyyy-MM-dd HH:mm:ss"
                      placeholder="дата начала действия"
                    />
      
                    <InputGroup.Addon> до </InputGroup.Addon>
      
                    <Form.Control
                      accepter={DatePicker}
                      block
                      oneTap
                      isoWeek
                      appearance="subtle"
                      name="activeUntil"
                      value={formState.activeUntil}
                      format="yyyy-MM-dd HH:mm:ss"
                      placeholder="дата окончания действия"
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Panel>
          </Col>
          <Col xs={24}>
            <Panel 
              shaded
              className='my-5'
            >
              <SelectLocale 
                selectedLanguage={selectedLanguage}
                onSelect={(language) => setSelectedLanguage(language)}
                onAdd={(language) => {
                  setFormState((state) => mergeLeft(state, { languages: [ ...state.languages, language ] }))
                }}
                languages={formState.languages}
              />
              <Checkbox 
                checked={formState.defaultLanguage === selectedLanguage}
                onChange={() => {
                  setFormState((state) => mergeLeft(state, { defaultLanguage: selectedLanguage }))
                }}
              >
                Язык по-умолчанию
              </Checkbox>
              <div className='border-2 border-gray-300 rounded-md p-3 mb-3'>
                <LocalizedForm
                  name='title'
                  label='Заголовок'
                  rows={2}
                  className='font-semibold pt-8 pb-12 font-family-georgia'
                  style={{ 
                    fontSize: '24px',
                    lineHeight: '24px',
                    border: '0px',
                    padding: '8px 12px',
                  }}
                  type='textarea'
                  selectedLanguage={selectedLanguage}
                  value={formState.i18n.find((translation) => translation.languageCode === selectedLanguage)?.title || ''}
                  onChange={(value: string) => {
                    const newTranslations = [ ...formState.i18n ]
                    const titleIndex = newTranslations.findIndex(translation => translation.languageCode === selectedLanguage)
                    if (titleIndex > -1) {
                      newTranslations[titleIndex].title = value
                    }
                    else {
                      newTranslations.push({ languageCode: selectedLanguage, title: value, lead: '', content: '' })
                    }
                    setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
                  }}
                />
              </div>
              <div className='border-2 border-gray-300 rounded-md p-3 mb-3'>
                <LocalizedForm
                  name='lead'
                  label='Лид'
                  type='textarea'
                  className='text-[18px] pt-8 pb-12 font-family-georgia'
                  style={{ 
                    border: '0px',
                    padding: '8px 12px',
                  }}
                  selectedLanguage={selectedLanguage}
                  value={formState.i18n.find((translation) => translation.languageCode === selectedLanguage)?.lead || ''}
                  onChange={(value: string) => {
                    const newTranslations = [ ...formState.i18n ]
                    const leadIndex = newTranslations.findIndex(translation => translation.languageCode === selectedLanguage)
                    if (leadIndex > -1) {
                      newTranslations[leadIndex].lead = value
                    }
                    else {
                      newTranslations.push({ languageCode: selectedLanguage, title: '', content: '', lead: value})
                    }
                    setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
                  }}
                />
              </div>
              <div className='p-8 border-2 border-gray-300 rounded-md'>
                <Form.ControlLabel>Контент </Form.ControlLabel>
                <div id='editor'></div>
              </div>

            </Panel>
          </Col>
        </Col>
        <Col xs={24} md={8}>
          <Panel
            shaded
            className='my-5'
          >
            { previewUrl ? (
              <DropzonePreview
                url={previewUrl}
                onDelete={handleUnlinkPreviewClick}
              />
            ) : (
              <Form.Group>
                <Form.ControlLabel>
                  Превью (jpg, png)
                </Form.ControlLabel>
                <DropzoneUploader
                  name={UPLOAD_NAME_TEXT_MATERIAL_IMAGE}
                  action={API_URL_UPLOAD_MATERIAL_PREVIEW_IMAGE}
                  onUploaded={handlePreviewUploaded}
                  multiple={false}
                />
              </Form.Group>
            )}
            <Form.Group>
              <Form.ControlLabel>Источник фото на превью</Form.ControlLabel>
              <Form.Control
                name='preview_url'
                placeholder='URL превью'
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Название источника превью</Form.ControlLabel>
              <Form.Control
                name='preview_source'
                placeholder='Источник превью'
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Вес (приоритет)</Form.ControlLabel>
              <Form.Control name="priority_weight" type="number" />
            </Form.Group>
          </Panel>
          <Panel
            shaded
          >
            <Form.Group>
              <Form.ControlLabel>Теги</Form.ControlLabel>
              <TextMaterialTagPicker 
                value={formState.tagIds}
                onSelect={(value) => {
                  setFormState((state) => mergeLeft(state, { tagIds: value }))
                }}
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Темы</Form.ControlLabel>
              <ThemeCheckPicker 
                value={formState.themeIds}
                onChange={(value) => {
                  setFormState((state) => mergeLeft(state, { themeIds: value }))
                }}
              />
            </Form.Group>
          </Panel>
        </Col>
       
      </Grid>

      <SaveButton
        loading={loadingTextMaterial || loadingSaveTextCategory}
        disabled={loadingTextMaterial || loadingSaveTextCategory}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
